import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaLeaf, FaTree, FaSeedling, FaRecycle, FaHome, FaLightbulb, FaHandshake, FaHeart, FaAward, FaCertificate, FaCheckCircle } from 'react-icons/fa';
import './About.css';

const teamMembers = [
  {
    name: 'Plant Care Specialist',
    role: 'Indoor Plant Expert',
    emoji: <FaLeaf />,
    exp: 'Plant Care',
    spec: 'Indoor Plants'
  },

  {
    name: 'Landscape Supervisor',
    role: 'Landscape & Garden Planning',
    emoji: <FaTree />,
    exp: 'Landscape',
    spec: 'Garden Design'
  },

  {
    name: 'Nursery Manager',
    role: 'Plant Inventory & Operations',
    emoji: <FaSeedling />,
    exp: 'Operations',
    spec: 'Plant Management'
  },

  {
    name: 'Plant Maintenance Expert',
    role: 'Plant Health & Maintenance',
    emoji: <FaLeaf />,
    exp: 'Maintenance',
    spec: 'Plant Health'
  }
];

const values = [
  {
    icon: <FaSeedling />,
    title: 'Quality Plants',
    desc: 'We provide healthy, premium-quality indoor, outdoor, flowering, herbal, and fruit plants grown with proper care.'
  },
  {
    icon: <FaRecycle />,
    title: 'Sustainability',
    desc: 'Our approach promotes eco-friendly gardening and sustainable green solutions for homes and businesses.'
  },
  {
    icon: <FaHome />,
    title: 'Plant Library',
    desc: 'Our unique Plant Library service allows customers to enjoy beautiful plants with easy replacement and maintenance support.'
  },
  {
    icon: <FaLightbulb />,
    title: 'Green Innovation',
    desc: 'We continuously introduce creative landscaping and plant décor solutions for modern living spaces.'
  },
  {
    icon: <FaHandshake />,
    title: 'Customer First',
    desc: 'Building long-term relationships through reliable service, expert guidance, and personalized plant solutions.'
  },
  {
    icon: <FaHeart />,
    title: 'Nature Connection',
    desc: 'Helping people reconnect with nature by creating greener and healthier environments everywhere.'
  }
];

const milestones = [
  {
    year: 'Beginning',
    title: 'A Vision for Green Spaces',
    desc: 'Started with a passion for plants and a commitment to promoting sustainable and greener lifestyles.'
  },

  {
    year: 'Growth',
    title: 'Expanding Plant Collections',
    desc: 'Introduced a wide range of indoor plants, outdoor plants, flowering plants, herbal plants, and fruit plants.'
  },

  {
    year: 'Innovation',
    title: 'Plant Library Concept',
    desc: 'Developed plant rental and replacement solutions to make greenery accessible and hassle-free for customers.'
  },

  {
    year: 'Services',
    title: 'Complete Green Solutions',
    desc: 'Expanded into landscaping, garden development, and plant maintenance services for residential and commercial spaces.'
  },

  {
    year: 'Today',
    title: 'Trusted Green Partner',
    desc: 'Continuing to help individuals and businesses create healthier, greener, and more sustainable environments.'
  }
];

const About = () => {
  return (
    <div className="page-wrapper about-page">

      {/* ════ ABOUT HERO — Full-Width Banner (Reference Style) ════ */}
      <section className="about-hero">
        {/* Background ambient particles */}
        <div className="about-hero-particle about-hero-particle--1" />
        <div className="about-hero-particle about-hero-particle--2" />
        <div className="about-hero-particle about-hero-particle--3" />

        <div className="about-hero-inner">
          {/* LEFT: Text & CTAs */}
          <div className="about-hero-left" style={{ animation: 'fadeInLeft 0.8s ease both' }}>
            <div className="about-hero-tag">
              <div className="about-tag-minimal">
                EST. 2022 • MOST TRUSTED Nursery
              </div>
            </div>
            <h1 className="about-hero-title">
              Bringing Nature <span className="about-hero-title-accent">Closer,</span>
              <br />
              <span className="gold-text"> One Plant </span>at a time
            </h1>
            <p className="about-hero-desc">
              Nisarg Plant Library Pvt. Ltd. is a trusted plant nursery and plant library dedicated to creating greener homes, offices, commercial spaces, and landscapes. We provide premium indoor plants, outdoor plants, flowering plants, herbal plants, plant rental services, landscaping solutions, and professional plant maintenance services.
            </p>
            <div className="about-hero-btns">
              <Link to="/contact" className="about-btn-primary">
                <span>Get In Touch</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
              </Link>
              <Link to="/services" className="about-btn-outline">Explore Services</Link>
            </div>
          </div>

          {/* RIGHT: Floating plant images */}
          <div className="about-hero-right" style={{ animation: 'fadeInRight 0.8s 0.2s ease both' }}>
            <div className="about-hero-plants">
              <div className="about-plant-img about-plant-img--main">
                <img src="/images/hero_plant.png" alt="Snake Plant" />
              </div>
            </div>
            {/* Floating badge */}
            <div className="about-hero-img-badge">
              <span className="about-hero-badge-icon"><FaAward /></span>
              <div>
                <div className="about-hero-badge-title">Award Winning</div>
                <div className="about-hero-badge-sub">Best Nursery 2022</div>
              </div>
            </div>
            <span className="about-hero-float about-hero-float--1">🌿</span>
            <span className="about-hero-float about-hero-float--2">🌺</span>
          </div>
        </div>

        {/* ── STATS BAR (reference image bottom bar) ── */}
        <div className="about-stats-bar">
          <div className="about-stats-bar-inner">
            <div className="about-stats-bar-item">
              <span className="about-stats-bar-num">Indoor</span>
              <span className="about-stats-bar-label">Plant Solutions</span>
            </div>
            <div className="about-stats-bar-divider" />
            <div className="about-stats-bar-item">
              <span className="about-stats-bar-num">Outdoor</span>
              <span className="about-stats-bar-label">Landscaping Plants</span>
            </div>
            <div className="about-stats-bar-divider" />
            <div className="about-stats-bar-item">
              <span className="about-stats-bar-num">Plant Library</span>
              <span className="about-stats-bar-label">Rental Services</span>
            </div>
            <div className="about-stats-bar-divider" />
            <div className="about-stats-bar-item">
              <span className="about-stats-bar-num">Maintenance</span>
              <span className="about-stats-bar-label">Professional Care</span>
            </div>
          </div>
        </div>
      </section>

      {/* ════ STORY SECTION ════ */}
      <section className="story-section">
        <div className="section-container">
          <div className="story-grid">
            <div className="story-img-wrap">
              <img src="/images/sc1.png" alt="Nisarg Nursery Greenhouse" className="story-img" />
              <div className="story-img-border" />
              <div className="story-stat-card">
                <div className="story-stat-icon"><FaLeaf /></div>
                <div className="story-stat-val">3+</div>
                <div className="story-stat-label">Years of Growing</div>
              </div>
            </div>
            <div className="story-content">
              <div className="section-tag"> The Journey of Nisarg Nursery</div>
              <h2 className="section-title story-section-title">
                Growing Green Spaces with <br />
                <span className="gold-text">Passion & Purpose</span>
              </h2>
              <p className="story-para">
                It all started in 2022 when our founder and certified botanist, Nisarg Patel, began growing rare tropical, air-purifying indoor plants in his backyard. Driven by a deep passion for eco-friendly gardening, what began as a small weekend hobby quickly evolved into the most beloved organic plant nursery in the region.
              </p>
              <p className="story-para">
                From indoor plants and outdoor landscaping to plant rental and maintenance solutions, we provide complete green solutions for homes, offices, hotels, hospitals, educational institutions, and commercial projects. Our team is committed to delivering healthy plants, expert guidance, and sustainable greenery solutions tailored to every space.
                <strong style={{ color: 'var(--accent-glow)' }}> Making premium, healthy plants accessible, affordable, and sustainable for every home and office.</strong>
              </p>
              <div className="story-badges">
                {[
                  { label: 'Organic Certified', icon: <FaCheckCircle /> },
                  { label: 'ISO 9001:2015', icon: <FaCertificate /> },
                  { label: 'Award Winning', icon: <FaAward /> },
                ].map(({ label, icon }) => (
                  <div key={label} className="story-badge">
                    <span>{icon}</span> {label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════ VALUES SECTION ════ */}
      <section className="values-section">
        <div className="section-container">
          <div className="section-header">
            <div className="section-tag"> The Nisarg Promise</div>
            <h2 className="section-title">Our Commitment to <span className="gold-text">Green Living</span></h2>
            <p className="section-subtitle">
              Our commitment to quality plants, sustainable practices, and customer satisfaction drives everything we do. Every project is designed to create beautiful, healthy, and long-lasting green environments.
            </p>
          </div>
          <div className="values-grid">
            {values.map(({ icon, title, desc }, i) => (
              <div key={title} className="value-card" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="value-icon">{icon}</div>
                <h3 className="value-title">{title}</h3>
                <p className="value-desc">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════ TIMELINE ════ */}
      <section className="timeline-section">
        <div className="section-container">
          <div className="section-header">
            <div className="section-tag"> Our Journey</div>
            <h2 className="section-title">Our Journey Towards <span className="gold-text">Greener Living</span></h2>
            <p className="section-subtitle">Nisarg Plant Library Pvt. Ltd. was established with a vision to make plants a natural part of everyday life. Over the years, we have helped homes, offices, commercial spaces, and institutions create healthier and more beautiful environments through quality plants, plant rental services, landscaping, and professional plant maintenance solutions.</p>
          </div>
          <div className="timeline">
            {milestones.map(({ year, title, desc }, i) => (
              <div key={year} className={`timeline-item ${i % 2 === 0 ? 'left' : 'right'}`}>
                <div className="timeline-card">
                  <div className="timeline-year">{year}</div>
                  <h3 className="timeline-title">{title}</h3>
                  <p className="timeline-desc">{desc}</p>
                </div>
                <div className="timeline-dot" />
              </div>
            ))}
            <div className="timeline-line" />
          </div>
        </div>
      </section>

      {/* ════ FOUNDERS SECTION ════ */}
      <section className="founders-section">
        <div className="section-container">
          <div className="section-header">
            <div className="section-tag"> Our Leadership</div>
            <h2 className="section-title">The <span className="gold-text">Visionaries</span></h2>
            <p className="section-subtitle">
              Meet the minds behind Nisarg Nursery's sustainable growth.
            </p>
          </div>
          <div className="founders-grid">
            <div className="founder-card">
              <div className="founder-img-wrapper">
                <img
                  src="/images/Founder N.png"
                  alt="Founder"
                  className="founder-img founder-main"
                />
              </div>
              <div className="founder-info">
                <h3 className="founder-name">Dilip Kathmale</h3>
                <p className="founder-role">Founder</p>
                <p className="founder-desc">With years of experience in plant care, landscaping, and green space development, our leadership focuses on delivering practical and sustainable plant solutions. Their dedication helps ensure that every project reflects quality, professionalism, and a genuine passion for nature.</p>
              </div>
            </div>
            <div className="founder-card">
              <div className="founder-img-wrapper">
                <img
                  src="/images/co-founder.png"
                  alt="Co-Founder"
                  className="founder-img cofounder-img"
                />
              </div>
              <div className="founder-info">
                <h3 className="founder-name">Vishal Rajmane</h3>
                <p className="founder-role">Co-Founder</p>
                <p className="founder-desc">Focused on customer experience and project execution, our leadership team works closely with clients to create customized indoor and outdoor plant solutions that enhance homes, workplaces, and commercial environments.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════ TEAM SECTION ════ */}
      <section className="team-section">
        <div className="section-container">
          <div className="section-header">
            <div className="section-tag"> Meet the Team</div>
            <h2 className="section-title">The <span className="gold-text">Green Souls</span> Behind It All</h2>
            <p className="section-subtitle">
              Our experienced team of plant specialists, horticulture experts, and landscape professionals work together to deliver exceptional green solutions for every project.
            </p>
          </div>
          <div className="team-grid">
            {teamMembers.map(({ name, role, emoji, exp, spec }) => (
              <div key={name} className="team-card">
                <div className="team-avatar">{emoji}</div>
                <div className="team-info">
                  <h3 className="team-name">{name}</h3>
                  <div className="team-role">{role}</div>
                  <div className="team-meta">
                    <span className="team-tag">{exp}</span>
                    <span className="team-tag">{spec}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════ CTA ════ */}
      <section className="about-cta">
        <div className="about-cta-inner">
          <h2 className="cta-title">
            Let's Create a Greener Future <span className="gold-text">Together</span>
          </h2>
          <p className="cta-desc">
            From plant rentals and maintenance to landscaping and premium plant collections, Nisarg Plant Library is ready to transform your space into a thriving green environment.
          </p>
          <div className="cta-btns">
            <Link to="/contact" className="btn-primary">
              <span>Contact Us Today</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
            </Link>
            <Link to="/services" className="btn-outline">Explore Services</Link>
          </div>
        </div>
      </section>
    </div>
  );
};
export default About;
